/**
 * The rail's icons, drawn inline from lucide's paths so their parts can
 * move: hover the item and the chat bubbles nudge apart, the landmark's
 * columns rise, the top layer lifts off its stack, the tray catches, the gear
 * turns; move away and the same motion plays backward. Driven by the Web
 * Animations API (railIconMotion.ts), so no animation runtime ships, and
 * nothing moves for people who asked for less motion.
 *
 * Each accepts the same `className` a lucide icon does; the animation is
 * armed by the nearest `group` ancestor (the rail item) and its parts are
 * marked `data-rail-motion`.
 *
 * @module components/icons/RailIcons
 */
import { useEffect, useRef } from "react";

import { cn } from "../../lib/utils";
import {
  createRailMotionPlayer,
  isRailMotionName,
  motionDuration,
  RAIL_MOTIONS,
} from "./railIconMotion";

const SVG_PROPS = {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

/**
 * Plays the svg's `data-rail-motion` parts forward while the enclosing
 * `group` is hovered and backward when the pointer leaves it.
 */
function useRailMotion() {
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    const svg = ref.current;
    const trigger = svg?.closest(".group");
    if (!svg || !trigger) return;
    const parts = Array.from(svg.querySelectorAll<SVGElement>("[data-rail-motion]")).flatMap(
      (element) => {
        const name = element.dataset.railMotion;
        if (!isRailMotionName(name)) return [];
        const delay = Number(element.dataset.railMotionDelay) || 0;
        return [{ element, motion: RAIL_MOTIONS[name], delay }];
      },
    );
    const span = Math.max(0, ...parts.map((part) => part.delay + motionDuration(part.motion)));
    const players = parts.map((part) =>
      createRailMotionPlayer(part.element, part.motion, { delay: part.delay, span }),
    );
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const enter = () => {
      if (reducedMotion.matches) return;
      for (const player of players) player.enter();
    };
    const leave = () => {
      for (const player of players) player.leave();
    };
    trigger.addEventListener("pointerenter", enter);
    trigger.addEventListener("pointerleave", leave);
    return () => {
      trigger.removeEventListener("pointerenter", enter);
      trigger.removeEventListener("pointerleave", leave);
      for (const player of players) player.stop();
    };
  }, []);
  return ref;
}

/** lucide `house`: the roof lifts on hover, like Governance's. */
export function HomeRailIcon({ className }: { className?: string }) {
  const ref = useRailMotion();
  return (
    <svg ref={ref} {...SVG_PROPS} className={cn("overflow-visible", className)}>
      <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
      <path d="M21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-9" />
      <path
        data-rail-motion="roof-lift"
        d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10"
      />
    </svg>
  );
}

/** lucide `messages-square`: two speech bubbles that nudge apart on hover. */
export function ChatRailIcon({ className }: { className?: string }) {
  const ref = useRailMotion();
  return (
    <svg ref={ref} {...SVG_PROPS} className={cn("overflow-visible", className)}>
      <path
        data-rail-motion="chat-front"
        d="M16 10a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 14.286V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"
      />
      <path
        data-rail-motion="chat-back"
        d="M20 9a2 2 0 0 1 2 2v10.286a.71.71 0 0 1-1.212.502l-2.202-2.202A2 2 0 0 0 17.172 19H10a2 2 0 0 1-2-2v-1"
      />
    </svg>
  );
}

/** lucide `landmark`: the columns sink and rise in turn and the roof lifts on hover. */
export function GovernanceRailIcon({ className }: { className?: string }) {
  const ref = useRailMotion();
  const column = (x: number, delay: number) => (
    <path
      key={x}
      d={`M${x} 18v-7`}
      pathLength={1}
      data-rail-motion="column-rise"
      data-rail-motion-delay={delay}
    />
  );
  return (
    <svg ref={ref} {...SVG_PROPS} className={cn("overflow-visible", className)}>
      <path
        data-rail-motion="roof-lift"
        d="M11.12 2.198a2 2 0 0 1 1.76.006l7.866 3.847c.476.233.31.949-.22.949H3.474c-.53 0-.695-.716-.22-.949z"
      />
      {column(6, 0)}
      {column(10, 60)}
      {column(14, 120)}
      {column(18, 180)}
      <path d="M3 22h18" />
    </svg>
  );
}

/** lucide `blocks`: the loose block lifts away from the stack on hover. */
export function AppsRailIcon({ className }: { className?: string }) {
  const ref = useRailMotion();
  return (
    <svg ref={ref} {...SVG_PROPS} className={cn("overflow-visible", className)}>
      <path d="M10 22V7a1 1 0 0 0-1-1H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5a1 1 0 0 0-1-1H2" />
      <rect data-rail-motion="roof-lift" x="14" y="2" width="8" height="8" rx="1" />
    </svg>
  );
}

/** lucide `layers`: the top sheet lifts off the stack, the middle one just after it, on hover. */
export function SpacesRailIcon({ className }: { className?: string }) {
  const ref = useRailMotion();
  return (
    <svg ref={ref} {...SVG_PROPS} className={cn("overflow-visible", className)}>
      <path
        data-rail-motion="roof-lift"
        d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z"
      />
      <path
        data-rail-motion="layer-lift"
        data-rail-motion-delay={60}
        d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12"
      />
      <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
    </svg>
  );
}

/** The tray dips as if something just landed in it. */
export function InboxRailIcon({ className }: { className?: string }) {
  const ref = useRailMotion();
  return (
    <svg ref={ref} {...SVG_PROPS} className={cn("overflow-visible", className)}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" data-rail-motion="inbox-catch" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
      <path d="M12 3v6m0 0-2-2m2 2 2-2" className="opacity-0" data-rail-motion="inbox-drop" />
    </svg>
  );
}

/** The gear turns a notch and stays turned until the pointer leaves. */
export function SettingsRailIcon({ className }: { className?: string }) {
  const ref = useRailMotion();
  return (
    <svg ref={ref} {...SVG_PROPS} className={cn("overflow-visible", className)}>
      <g className="origin-center" data-rail-motion="gear-turn">
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
        <circle cx="12" cy="12" r="3" />
      </g>
    </svg>
  );
}

/** lucide `newspaper`: the masthead lifts and the story lines are written in again. Home's Updates card. */
export function UpdatesGlanceIcon({ className }: { className?: string }) {
  const ref = useRailMotion();
  const line = (d: string, delay: number) => (
    <path d={d} pathLength={1} data-rail-motion="column-rise" data-rail-motion-delay={delay} />
  );
  return (
    <svg ref={ref} {...SVG_PROPS} className={cn("overflow-visible", className)}>
      <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-4 0v-9a2 2 0 0 1 2-2h2" />
      <rect data-rail-motion="roof-lift" width="8" height="4" x="10" y="6" rx="1" />
      {line("M10 14h8", 80)}
      {line("M10 18h5", 160)}
    </svg>
  );
}

/** lucide `gauge`: the needle sweeps across the dial and settles. Home's Allowance card. */
export function AllowanceGlanceIcon({ className }: { className?: string }) {
  const ref = useRailMotion();
  return (
    <svg ref={ref} {...SVG_PROPS} className={cn("overflow-visible", className)}>
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
      <path d="m12 14 4-4" data-rail-motion="needle-sweep" style={{ transformOrigin: "12px 14px" }} />
    </svg>
  );
}
